import { useTranslation } from 'react-i18next';
import { ActivityType, ChannelTypeInternal, UserAction } from '../../../../data-access/gql-types/graphql';
import { toastInfo } from '../../../../utils/toast';
import { useActionBatch } from '../../api/action/action.hooks';
import {
  ActionBatchBody,
  BatchChannel,
  CoverDirectionBatchChannel,
  CoverPositionBatchChannel,
  GateDirectionBatchChannel,
  GatePositionBatchChannel,
  LightBrightnessBatchChannel,
  LightRGBBatchChannel,
  SwitchLightOnOffBatchChannel,
  TypeOfActionType,
  GateDailyModeBatchChannel,
  GatePartialControlModeBatchChannel,
} from '../../api/action/action.types';
import { useDevicesAndChannelsContext } from '../../context/devices-and-channels';
import { DashboardUserAction } from '../../types';
import { CoverDirection, GateDirection } from '../../types/channel';

export const useAdvancedAction = (userAction: DashboardUserAction | UserAction) => {
  const { t } = useTranslation('action');
  const actionBatch = useActionBatch();
  const { channelList } = useDevicesAndChannelsContext();

  const handleAction = () => {
    const allControls = userAction.action.channelActions
      .map((channelAction) => {
        const deviceId = channelList.find((x) => x.id === channelAction.channelId)?.deviceId;

        if (deviceId) {
          const channelData = { deviceId, channelId: channelAction.channelId };

          switch (channelAction.activityType) {
            case ActivityType.On:
              return {
                ...channelData,
                value: true,
                $type:
                  channelAction.channelType === ChannelTypeInternal.Light
                    ? TypeOfActionType.BatchLightToggleOnOffRequest
                    : TypeOfActionType.BatchSwitchToggleOnOffRequest,
              } as SwitchLightOnOffBatchChannel;
            case ActivityType.Off:
              return {
                ...channelData,
                value: false,
                $type:
                  channelAction.channelType === ChannelTypeInternal.Light
                    ? TypeOfActionType.BatchLightToggleOnOffRequest
                    : TypeOfActionType.BatchSwitchToggleOnOffRequest,
              } as SwitchLightOnOffBatchChannel;
            case ActivityType.Open: {
              if (channelAction.channelType === ChannelTypeInternal.Blind) {
                return {
                  ...channelData,
                  direction: CoverDirection.open,
                  $type: TypeOfActionType.BatchBlindSetDirectionRequest,
                } as CoverDirectionBatchChannel;
              }

              if (channelAction.channelType === ChannelTypeInternal.Gate) {
                return {
                  ...channelData,
                  direction: GateDirection.open,
                  $type: TypeOfActionType.BatchGateSetDirectionRequest,
                } as GateDirectionBatchChannel;
              }

              return;
            }
            case ActivityType.Close: {
              if (channelAction.channelType === ChannelTypeInternal.Blind) {
                return {
                  ...channelData,
                  direction: CoverDirection.close,
                  $type: TypeOfActionType.BatchBlindSetDirectionRequest,
                } as CoverDirectionBatchChannel;
              }

              if (channelAction.channelType === ChannelTypeInternal.Gate) {
                return {
                  ...channelData,
                  direction: GateDirection.close,
                  $type: TypeOfActionType.BatchGateSetDirectionRequest,
                } as GateDirectionBatchChannel;
              }

              return;
            }
            case ActivityType.Stop: {
              if (channelAction.channelType === ChannelTypeInternal.Blind) {
                return {
                  ...channelData,
                  direction: CoverDirection.stop,
                  $type: TypeOfActionType.BatchBlindSetDirectionRequest,
                } as CoverDirectionBatchChannel;
              }

              if (channelAction.channelType === ChannelTypeInternal.Gate) {
                return {
                  ...channelData,
                  direction: GateDirection.stop,
                  $type: TypeOfActionType.BatchGateSetDirectionRequest,
                } as GateDirectionBatchChannel;
              }

              return;
            }

            case ActivityType.SetPosition: {
              if (channelAction.channelType === ChannelTypeInternal.Blind) {
                return {
                  ...channelData,
                  position: parseInt(channelAction.value),
                  $type: TypeOfActionType.BatchBlindSetPositionRequest,
                } as CoverPositionBatchChannel;
              }

              if (channelAction.channelType === ChannelTypeInternal.Gate) {
                return {
                  ...channelData,
                  position: parseInt(channelAction.value),
                  $type: TypeOfActionType.BatchGateSetPositionRequest,
                } as GatePositionBatchChannel;
              }

              return;
            }

            case ActivityType.SetDim:
              return {
                ...channelData,
                brightness: parseInt(channelAction.value),
                $type: TypeOfActionType.BatchLightSetBrightnessRequest,
              } as LightBrightnessBatchChannel;
            case ActivityType.SetLightColor: {
              const [R, G, B] = channelAction.value.split(',').map((x) => parseInt(x));

              return {
                ...channelData,
                r: R,
                g: G,
                b: B,
                $type: TypeOfActionType.BatchLightSetRgbRequest,
              } as LightRGBBatchChannel;
            }
            case ActivityType.Tilt:
              return {
                ...channelData,
                $type: TypeOfActionType.BatchGateTiltRequest,
              } as BatchChannel;
            case ActivityType.SetDailyModeOn:
              return {
                ...channelData,
                value: true,
                $type: TypeOfActionType.BatchGateSetDailyModeRequest,
              } as GateDailyModeBatchChannel;
            case ActivityType.SetDailyModeOff:
              return {
                deviceId,
                channelId: channelAction.channelId,
                value: false,
                $type: TypeOfActionType.BatchGateSetDailyModeRequest,
              } as GateDailyModeBatchChannel;
            case ActivityType.SetPartialControlModeOn:
              return {
                ...channelData,
                value: true,
                $type: TypeOfActionType.BatchGateSetPartialControlModeRequest,
              } as GatePartialControlModeBatchChannel;
            case ActivityType.SetPartialControlModeOff:
              return {
                ...channelData,
                value: false,
                $type: TypeOfActionType.BatchGateSetPartialControlModeRequest,
              } as GatePartialControlModeBatchChannel;
          }
        }
      })
      .filter((x) => x?.channelId);

    if (allControls.length) {
      actionBatch.mutate({
        controls: allControls,
      } as ActionBatchBody);
    }
  };

  const setActionOn = () => {
    if (!userAction?.action.channelActions.length) {
      toastInfo({ content: t('trigger.validationErrors.deviceNotFound') });
      return;
    }

    handleAction();
  };

  return { setActionOn };
};
